/**
 * useAnnotationPositions
 *
 * Computes vertical positions for floating annotation cards (comments and
 * track changes) relative to the editor scroll container. Each card is
 * placed at the vertical position of the text it references.
 *
 * When multiple cards overlap, a single-pass algorithm pushes them down
 * so they stack with a gap — identical to Google Docs behavior.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Editor } from "@tiptap/core";
import { CommentData } from "../TipTapEditor";
import { TrackChangeData } from "../TrackChangesPanel";

export interface AnnotationPosition {
  id: string;
  kind: "comment" | "trackChange";
  /** Natural top offset relative to scroll container content */
  targetTop: number;
  /** Adjusted top after overlap resolution */
  adjustedTop: number;
  /** Measured card height (updated after first render) */
  cardHeight: number;
  /** The underlying data */
  comment?: CommentData;
  trackChange?: TrackChangeData;
}

const CARD_GAP = 6; // px between stacked cards
const DEFAULT_CARD_HEIGHT = 56; // estimated collapsed card height
const EXPANDED_CARD_HEIGHT = 140; // estimated expanded card height
const PENDING_CARD_HEIGHT = 160; // pending comment card with textarea
const RECALC_DEBOUNCE_MS = 50;

/**
 * Resolve overlapping cards by pushing them down.
 * Single-pass O(n) algorithm on sorted array.
 */
function resolveOverlaps(positions: AnnotationPosition[]): AnnotationPosition[] {
  if (positions.length === 0) return positions;

  // Sort by targetTop (stable — preserves original order for same-position items)
  const sorted = [...positions].sort((a, b) => a.targetTop - b.targetTop);

  sorted[0].adjustedTop = sorted[0].targetTop;

  for (let i = 1; i < sorted.length; i++) {
    const prevBottom = sorted[i - 1].adjustedTop + sorted[i - 1].cardHeight + CARD_GAP;
    sorted[i].adjustedTop = Math.max(sorted[i].targetTop, prevBottom);
  }

  return sorted;
}

/**
 * Get the vertical offset of a DOM element relative to the scroll container's
 * content origin (i.e. where it would be at scrollTop=0).
 */
function getElementTopInScrollContainer(
  element: HTMLElement,
  scrollContainer: HTMLElement
): number {
  const elRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  return elRect.top - containerRect.top + scrollContainer.scrollTop;
}

export function useAnnotationPositions(
  editor: Editor | null,
  comments: CommentData[],
  trackChanges: TrackChangeData[],
  activeCommentId: string | null,
  activeChangeId: string | null,
  showComments: boolean,
  showTrackChanges: boolean,
  pendingCommentId?: string | null
): {
  positions: AnnotationPosition[];
  measureRef: (id: string, el: HTMLElement | null) => void;
} {
  const [positions, setPositions] = useState<AnnotationPosition[]>([]);
  const cardHeightsRef = useRef<Map<string, number>>(new Map());
  const recalcTimerRef = useRef<number | null>(null);

  /** Ref callback for measuring actual card heights after render */
  const measureRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      const height = el.getBoundingClientRect().height;
      if (height > 0) {
        cardHeightsRef.current.set(id, height);
      }
    } else {
      cardHeightsRef.current.delete(id);
    }
  }, []);

  const recalculate = useCallback(() => {
    if (!editor || editor.isDestroyed) return;

    const editorDom = editor.view.dom;
    const scrollContainer = editorDom.closest(".tiptap-editor-scroll-wrapper") as HTMLElement;
    if (!scrollContainer) return;

    const newPositions: AnnotationPosition[] = [];

    // Process comments
    if (showComments) {
      for (const comment of comments) {
        const el = editorDom.querySelector(
          `[data-comment-id="${comment.id}"]`
        ) as HTMLElement;

        if (el) {
          const targetTop = getElementTopInScrollContainer(el, scrollContainer);
          const isActive = activeCommentId === comment.id;
          const measuredHeight = cardHeightsRef.current.get(comment.id);
          const estimatedHeight = measuredHeight || (isActive ? EXPANDED_CARD_HEIGHT : DEFAULT_CARD_HEIGHT);

          newPositions.push({
            id: comment.id,
            kind: "comment",
            targetTop,
            adjustedTop: targetTop, // will be resolved
            cardHeight: estimatedHeight,
            comment,
          });
        }
      }
    }

    // Process track changes
    if (showTrackChanges) {
      for (const change of trackChanges) {
        const el = editorDom.querySelector(
          `[data-change-id="${change.id}"]`
        ) as HTMLElement;

        if (el) {
          const targetTop = getElementTopInScrollContainer(el, scrollContainer);
          const isActive = activeChangeId === change.id;
          const measuredHeight = cardHeightsRef.current.get(change.id);
          const estimatedHeight = measuredHeight || (isActive ? EXPANDED_CARD_HEIGHT : DEFAULT_CARD_HEIGHT);

          newPositions.push({
            id: change.id,
            kind: "trackChange",
            targetTop,
            adjustedTop: targetTop,
            cardHeight: estimatedHeight,
            trackChange: change,
          });
        }
      }
    }

    // Process pending comment (the mark is in the DOM but not yet in the comments array)
    if (pendingCommentId) {
      const el = editorDom.querySelector(
        `[data-comment-id="${pendingCommentId}"]`
      ) as HTMLElement;

      if (el) {
        const targetTop = getElementTopInScrollContainer(el, scrollContainer);
        const measuredHeight = cardHeightsRef.current.get(pendingCommentId);

        newPositions.push({
          id: pendingCommentId,
          kind: "comment",
          targetTop,
          adjustedTop: targetTop,
          cardHeight: measuredHeight || PENDING_CARD_HEIGHT,
          // no comment data — rendered as pending card by the layer
        });
      }
    }

    const resolved = resolveOverlaps(newPositions);
    setPositions(resolved);
  }, [editor, comments, trackChanges, activeCommentId, activeChangeId, showComments, showTrackChanges, pendingCommentId]);

  // Debounced recalc
  const scheduleRecalc = useCallback(() => {
    if (recalcTimerRef.current !== null) {
      cancelAnimationFrame(recalcTimerRef.current);
    }
    recalcTimerRef.current = requestAnimationFrame(() => {
      recalculate();
      recalcTimerRef.current = null;
    });
  }, [recalculate]);

  // Recalculate when data changes
  useEffect(() => {
    scheduleRecalc();
  }, [comments, trackChanges, activeCommentId, activeChangeId, showComments, showTrackChanges, pendingCommentId, scheduleRecalc]);

  // Recalculate on editor updates (content changed, so DOM positions shift)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    const handleUpdate = () => scheduleRecalc();
    editor.on("update", handleUpdate);
    editor.on("selectionUpdate", handleUpdate);

    return () => {
      editor.off("update", handleUpdate);
      editor.off("selectionUpdate", handleUpdate);
    };
  }, [editor, scheduleRecalc]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => scheduleRecalc();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [scheduleRecalc]);

  // Re-run overlap resolution after card heights are measured (second render pass)
  useEffect(() => {
    if (positions.length === 0) return;
    const timer = setTimeout(() => {
      // Check if any measured heights differ from what we used
      let needsUpdate = false;
      for (const pos of positions) {
        const measured = cardHeightsRef.current.get(pos.id);
        if (measured && Math.abs(measured - pos.cardHeight) > 2) {
          needsUpdate = true;
          break;
        }
      }
      if (needsUpdate) {
        recalculate();
      }
    }, RECALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [positions, recalculate]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (recalcTimerRef.current !== null) {
        cancelAnimationFrame(recalcTimerRef.current);
      }
    };
  }, []);

  return { positions, measureRef };
}
